import type * as React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/utils/utils";

export type TScrollableRowProps = {
    children: React.ReactNode;
    /** Names the scrolling region for assistive tech. */
    "aria-label": string;
    /** Extra classes for the scrolling viewport itself (spacing, gap, and so on). */
    className?: string;
};

/** Sub-pixel layout means scrollLeft rarely lands exactly on 0 or on the maximum. */
const EDGE_TOLERANCE = 1;

/**
 * A single row that scrolls sideways when its contents do not fit, and says so.
 *
 * The affordance is an edge fade plus a small chevron: the fade reads as "there is
 * more this way", the chevron gives a mouse user something to click, and the thin
 * scrollbar track covers platforms that draw one. Both appear only while something is
 * genuinely hidden — an affordance for scrolling that cannot scroll is worse than none.
 *
 * Keyboard users are already served without any of it: every control inside the row
 * is in the tab order and browsers scroll focused elements into view, which is why the
 * chevrons are hidden from assistive tech rather than added to it.
 */
export function ScrollableRow(props: TScrollableRowProps): React.ReactElement {
    const { children, className, "aria-label": ariaLabel } = props;

    const viewportRef = useRef<HTMLDivElement>(null);
    const [canScrollBack, setCanScrollBack] = useState(false);
    const [canScrollForward, setCanScrollForward] = useState(false);

    const sync = useCallback(() => {
        const el = viewportRef.current;
        if (el === null) return;
        const furthest = el.scrollWidth - el.clientWidth;
        setCanScrollBack(el.scrollLeft > EDGE_TOLERANCE);
        setCanScrollForward(el.scrollLeft < furthest - EDGE_TOLERANCE);
    }, []);

    useEffect(() => {
        const el = viewportRef.current;
        if (el === null) return;

        sync();

        // The row's own width changes with the window, and its contents change as
        // actions become available on a file, so both are watched.
        const observer = new ResizeObserver(sync);
        observer.observe(el);
        for (const child of Array.from(el.children)) {
            observer.observe(child);
        }
        return () => observer.disconnect();
    }, [sync, children]);

    const step = useCallback((direction: 1 | -1) => {
        const el = viewportRef.current;
        if (el === null) return;
        // Just under a full viewport, so the button that was at the edge stays visible
        // and the user keeps their place instead of jumping past it.
        const distance = Math.max(160, el.clientWidth * 0.8);
        el.scrollBy({ left: direction * distance, behavior: "smooth" });
    }, []);

    return (
        <div className="relative min-w-0">
            <div
                ref={viewportRef}
                data-testid="scrollable-row-viewport"
                role="group"
                aria-label={ariaLabel}
                onScroll={sync}
                className={cn(
                    "flex flex-nowrap items-center overflow-x-auto",
                    // Without this, flex would shrink the children to fit and the row
                    // would squash instead of scroll — no overflow, no affordance.
                    "[&>*]:shrink-0",
                    // A visible track is the honest, always-there signal on platforms
                    // that render one; the fades and chevrons cover the rest.
                    "[scrollbar-width:thin]",
                    className,
                )}
            >
                {children}
            </div>

            {canScrollBack && (
                <>
                    <div
                        aria-hidden
                        className="pointer-events-none absolute inset-y-0 left-0 z-10 w-12 bg-gradient-to-r from-background via-background/80 to-transparent"
                    />
                    <button
                        type="button"
                        tabIndex={-1}
                        aria-hidden="true"
                        data-testid="scrollable-row-scroll-left"
                        onClick={() => step(-1)}
                        className="absolute left-0 top-1/2 z-20 -translate-y-1/2 rounded p-0.5 text-muted-foreground transition-colors hover:text-foreground"
                    >
                        <ChevronLeft className="h-3.5 w-3.5" />
                    </button>
                </>
            )}

            {canScrollForward && (
                <>
                    <div
                        aria-hidden
                        className="pointer-events-none absolute inset-y-0 right-0 z-10 w-12 bg-gradient-to-l from-background via-background/80 to-transparent"
                    />
                    <button
                        type="button"
                        tabIndex={-1}
                        aria-hidden="true"
                        data-testid="scrollable-row-scroll-right"
                        onClick={() => step(1)}
                        className="absolute right-0 top-1/2 z-20 -translate-y-1/2 rounded p-0.5 text-muted-foreground transition-colors hover:text-foreground"
                    >
                        <ChevronRight className="h-3.5 w-3.5" />
                    </button>
                </>
            )}
        </div>
    );
}
